import type { HireExtraTeamMember } from "~/lib/marketplace/hire-recommendations-pricing";
import type { HireTeamMemberFormValues } from "~/components/marketplace/hire-team-member-dialog";

export type HireTeamRosterRole = "employer" | "lead" | "other";

export type HireTeamRosterEntry = {
  role: HireTeamRosterRole;
  name: string;
  email: string;
  title?: string;
  phone?: string;
  imageUrl?: string;
};

export function memberDisplayName(values: {
  displayName?: string;
  firstName?: string;
  lastName?: string;
}): string {
  const fromDisplay = values.displayName?.trim();
  if (fromDisplay) return fromDisplay;
  return `${values.firstName ?? ""} ${values.lastName ?? ""}`.trim();
}

export function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
  const trimmed = displayName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

export function formValuesToExtraMember(values: HireTeamMemberFormValues): HireExtraTeamMember {
  const name = memberDisplayName(values);
  return {
    name,
    email: values.email.trim(),
    title: values.title?.trim() || undefined,
    phone: values.phone?.trim() || undefined,
    imageUrl: values.imageUrl?.trim() || undefined,
  };
}

export function extraMemberToFormValues(member: HireExtraTeamMember): HireTeamMemberFormValues {
  const { firstName, lastName } = splitDisplayName(member.name);
  return {
    displayName: member.name,
    firstName,
    lastName,
    email: member.email,
    title: member.title ?? "",
    phone: member.phone ?? "",
    imageUrl: member.imageUrl ?? "",
  };
}

export function buildHireTeamRoster(options: {
  employer?: HireTeamMemberFormValues | null;
  lead?: HireTeamMemberFormValues | null;
  extras?: HireExtraTeamMember[];
}): HireTeamRosterEntry[] {
  const roster: HireTeamRosterEntry[] = [];
  const seen = new Set<string>();

  const push = (entry: HireTeamRosterEntry) => {
    const key = entry.email.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    roster.push(entry);
  };

  if (options.employer?.email.trim()) {
    push({
      role: "employer",
      name: memberDisplayName(options.employer),
      email: options.employer.email.trim(),
      title: options.employer.title?.trim() || undefined,
      phone: options.employer.phone?.trim() || undefined,
      imageUrl: options.employer.imageUrl?.trim() || undefined,
    });
  }

  if (options.lead?.email.trim()) {
    push({
      role: "lead",
      name: memberDisplayName(options.lead),
      email: options.lead.email.trim(),
      title: options.lead.title?.trim() || undefined,
      phone: options.lead.phone?.trim() || undefined,
      imageUrl: options.lead.imageUrl?.trim() || undefined,
    });
  }

  for (const member of options.extras ?? []) {
    if (!member.email.trim()) continue;
    push({
      role: "other",
      name: member.name.trim() || member.email.trim(),
      email: member.email.trim(),
      title: member.title,
      phone: member.phone,
      imageUrl: member.imageUrl,
    });
  }

  return roster;
}

export function rosterEntryLabel(entry: HireTeamRosterEntry): string {
  const title = entry.title?.trim();
  return title ? `${entry.name} (${title})` : entry.name;
}
